import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Plus, Trash2, ChevronLeft, Check, RefreshCw, Save, Baby, Printer } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_TIPS_HTML = ''; // placeholder

function escapeHtml(s) {
  if (!s) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function printPattern(pattern, categoryLabel, categoryEmoji, level) {
  if (!pattern) return;
  const materials = (pattern.materials || []).map((m) =>
    `<li class="check-item"><span class="check-box"></span><span>${escapeHtml(m)}</span></li>`
  ).join('');
  const tips = (pattern.tips || []).map((t) => `<li>${escapeHtml(t)}</li>`).join('');
  const steps = (pattern.steps || []).map((s, i) => `
    <div class="step">
      <div class="step-num">${i + 1}</div>
      <div class="step-body">
        <div class="step-title">${escapeHtml(s.title || `Steg ${i + 1}`)}</div>
        <div class="step-text">${escapeHtml(s.instructions || '')}</div>
      </div>
      <div class="step-check"></div>
    </div>`).join('');

  const html = `<!DOCTYPE html>
<html lang="sv"><head><meta charset="utf-8">
<title>${escapeHtml(pattern.title || 'Pyssel-mönster')}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Fredoka:wght@500;700&family=Nunito:wght@400;600&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Nunito', system-ui, sans-serif; margin: 0; padding: 24px; background: #fff; color: #111; }
  h1, h2, h3 { font-family: 'Fredoka', system-ui, sans-serif; margin: 0; }
  .sheet { max-width: 780px; margin: 0 auto; }
  .header {
    border: 3px solid #000; border-radius: 24px; padding: 18px 22px;
    background: linear-gradient(135deg, #FFC8DD 0%, #FDE8F5 50%, #E0F4FF 100%);
    display: flex; align-items: center; gap: 14px; margin-bottom: 18px;
  }
  .header .emoji { font-size: 46px; }
  .header h1 { font-size: 28px; line-height: 1.1; }
  .header .meta { font-size: 12px; opacity: 0.75; margin-top: 4px; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
  .card { border: 2px solid #000; border-radius: 18px; padding: 12px 14px; }
  .card.mat { background: #FFE8D6; }
  .card.tips { background: #FEF3C7; }
  .card.pic { background: #E8F4FF; text-align: center; min-height: 180px; display: flex; align-items: center; justify-content: center; flex-direction: column; }
  .card h3 { font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.08em; }
  ul { list-style: none; padding: 0; margin: 0; }
  li { font-size: 13px; margin: 4px 0; }
  .check-item { display: flex; align-items: flex-start; gap: 8px; }
  .check-box {
    display: inline-block; width: 14px; height: 14px;
    border: 2px solid #000; border-radius: 4px; flex-shrink: 0; margin-top: 1px;
  }
  .card.tips li { font-style: italic; }
  .pic-frame {
    border: 3px dashed #444; border-radius: 14px; width: 80%; height: 130px;
    display: flex; align-items: center; justify-content: center;
    color: #888; font-size: 12px;
  }
  .steps-title {
    font-family: 'Fredoka'; font-size: 16px; margin: 14px 0 8px;
    text-transform: uppercase; letter-spacing: 0.08em;
  }
  .step {
    display: flex; gap: 12px; align-items: flex-start;
    border: 2px solid #000; border-radius: 16px; padding: 10px 12px;
    margin-bottom: 8px; background: #fff; page-break-inside: avoid;
  }
  .step-num {
    width: 36px; height: 36px; flex-shrink: 0;
    border-radius: 50%; border: 3px solid #000;
    background: #FFE8A3; display: flex; align-items: center; justify-content: center;
    font-family: 'Fredoka'; font-weight: 700; font-size: 18px;
  }
  .step-body { flex: 1; min-width: 0; }
  .step-title { font-family: 'Fredoka'; font-weight: 700; font-size: 14px; margin-bottom: 2px; }
  .step-text { font-size: 13px; line-height: 1.4; white-space: pre-wrap; }
  .step-check {
    width: 24px; height: 24px; flex-shrink: 0; border: 3px solid #000;
    border-radius: 6px; margin-top: 6px; background: #fff;
  }
  .finish {
    margin-top: 14px; border: 3px solid #000; border-radius: 20px; padding: 14px;
    background: linear-gradient(135deg, #CDB4DB 0%, #FFC8DD 100%);
  }
  .finish h3 { font-size: 14px; margin-bottom: 6px; }
  .footer { text-align: center; margin-top: 20px; font-size: 11px; opacity: 0.6; }
  @media print {
    body { padding: 0; }
    .step { page-break-inside: avoid; }
    .noprint { display: none !important; }
  }
</style>
</head><body>
<div class="sheet">
  <div class="header">
    <div class="emoji">${categoryEmoji || '🧶'}</div>
    <div>
      <h1>${escapeHtml(pattern.title || '')}</h1>
      <div class="meta">${escapeHtml(categoryLabel || '')} · ${escapeHtml(level || '')} · ${escapeHtml(pattern.time_estimate || '')} · ${escapeHtml(pattern.difficulty || '')}</div>
      ${pattern.intro ? `<div style="font-size:12px;margin-top:6px;">${escapeHtml(pattern.intro)}</div>` : ''}
    </div>
  </div>

  <div class="grid2">
    <div class="card mat">
      <h3>🧺 Material</h3>
      <ul>${materials}</ul>
    </div>
    ${tips ? `<div class="card tips"><h3>💡 Tips</h3><ul>${tips}</ul></div>` : `<div class="card pic"><div class="pic-frame">Klistra in eller rita bild här 📸</div></div>`}
  </div>

  <div class="steps-title">🪡 Steg för steg</div>
  ${steps}

  ${pattern.finishing ? `<div class="finish"><h3>✨ Avslutning</h3><div style="font-size:13px;">${escapeHtml(pattern.finishing)}</div></div>` : ''}

  <div class="footer">Skapad med Focus Critter 🐾 · ${new Date().toLocaleDateString('sv-SE')}</div>
</div>
<script>
  window.addEventListener('load', function() {
    setTimeout(function(){ window.print(); }, 400);
  });
</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function PatternView({ pattern, progress, onToggle, category }) {
  return (
    <div className="space-y-4">
      {pattern.intro && (
        <div className="neo-card p-4 bg-[#FFE8D6]">
          <p className="text-sm">{pattern.intro}</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="neo-card p-2 bg-[#FFF3B0] text-center">
          <p className="opacity-70">Tid</p>
          <p className="font-bold">{pattern.time_estimate || '—'}</p>
        </div>
        <div className="neo-card p-2 bg-[#BBF7D0] text-center">
          <p className="opacity-70">Svårighet</p>
          <p className="font-bold">{pattern.difficulty || '—'}</p>
        </div>
        <div className="neo-card p-2 bg-[#A2D2FF] text-center">
          <p className="opacity-70">Steg</p>
          <p className="font-bold">{pattern.steps?.length || 0}</p>
        </div>
        <div className="neo-card p-2 bg-[#FFC8DD] text-center">
          <p className="opacity-70">Klart</p>
          <p className="font-bold">
            {progress ? `${progress.filter(Boolean).length}/${progress.length}` : '—'}
          </p>
        </div>
      </div>

      {pattern.materials?.length > 0 && (
        <div className="neo-card p-4 bg-[#FDE8F5]">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-2">🧺 Material</p>
          <ul className="text-sm space-y-1">
            {pattern.materials.map((m, i) => (
              <li key={i} className="flex gap-2"><span className="font-bold text-pink-600">•</span>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {pattern.tips?.length > 0 && (
        <div className="neo-card p-4 bg-[#FEF3C7]">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-2">💡 Tips</p>
          <ul className="text-sm space-y-1">
            {pattern.tips.map((t, i) => <li key={i} className="italic">— {t}</li>)}
          </ul>
        </div>
      )}

      {pattern.steps?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 px-1">🪡 Steg för steg</p>
          {pattern.steps.map((s, i) => {
            const done = progress?.[i];
            return (
              <div key={i}
                className={`neo-card p-4 flex gap-3 items-start transition ${done ? 'bg-[#BBF7D0] opacity-80' : 'bg-white'}`}
                data-testid={`pyssel-step-${i}`}>
                <button onClick={() => onToggle?.(i, !done)}
                  className={`neo-btn w-9 h-9 shrink-0 flex items-center justify-center font-bold text-sm ${done ? 'bg-green-500 text-white' : 'bg-white'}`}
                  data-testid={`pyssel-step-toggle-${i}`}>
                  {done ? <Check className="w-4 h-4" strokeWidth={3} /> : (i + 1)}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-bold font-['Fredoka'] text-sm">{s.title || `Steg ${i + 1}`}</p>
                  <p className="text-sm mt-0.5 whitespace-pre-wrap">{s.instructions}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pattern.finishing && (
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFC8DD] to-[#CDB4DB]">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">✨ Avslutning</p>
          <p className="text-sm">{pattern.finishing}</p>
        </div>
      )}
    </div>
  );
}

function GenerateForm({ categories, levels, onGenerate, generating }) {
  const [category, setCategory] = useState('crochet');
  const [idea, setIdea] = useState('');
  const [level, setLevel] = useState('beginner');
  const [kidsFriendly, setKidsFriendly] = useState(true);
  const [timeMinutes, setTimeMinutes] = useState(60);

  const submit = (e) => {
    e.preventDefault();
    if (!idea.trim()) { toast.error('Skriv en idé först'); return; }
    onGenerate({ category, idea, level, kids_friendly: kidsFriendly, time_minutes: timeMinutes });
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="pyssel-generate-form">
      <div>
        <p className="text-xs font-bold mb-2">Kategori</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {categories.map((c) => (
            <button key={c.id} type="button" onClick={() => setCategory(c.id)}
              className={`p-3 rounded-2xl border-2 border-black text-left transition ${category === c.id ? 'bg-[#FFC8DD] scale-[1.02]' : 'bg-white'}`}
              data-testid={`pyssel-cat-${c.id}`}>
              <p className="text-xl">{c.emoji}</p>
              <p className="text-xs font-bold font-['Fredoka']">{c.label}</p>
              <p className="text-[10px] opacity-70">{c.sub}</p>
            </button>
          ))}
        </div>
      </div>

      <label className="block text-xs font-bold">Vad ska vi göra?
        <input value={idea} onChange={(e) => setIdea(e.target.value)}
          placeholder="t.ex. en söt igelkott, en mössa med tofs, en dinosaurie…"
          maxLength={200}
          className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white text-sm"
          data-testid="pyssel-idea-input" />
      </label>

      <div>
        <p className="text-xs font-bold mb-2">Nivå</p>
        <div className="flex flex-wrap gap-2">
          {levels.map((l) => (
            <button key={l.id} type="button" onClick={() => setLevel(l.id)}
              className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold ${level === l.id ? 'bg-[#BBF7D0] scale-105' : 'bg-white'}`}
              data-testid={`pyssel-level-${l.id}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-xs font-bold">
          <input type="checkbox" checked={kidsFriendly} onChange={(e) => setKidsFriendly(e.target.checked)}
            className="w-4 h-4 accent-pink-500" data-testid="pyssel-kids-toggle" />
          <Baby className="w-3.5 h-3.5" /> Barnvänliga instruktioner
        </label>
        <label className="flex items-center gap-2 text-xs font-bold">
          Tid:
          <input type="number" min={10} max={480} value={timeMinutes}
            onChange={(e) => setTimeMinutes(Math.max(10, Math.min(480, Number(e.target.value) || 60)))}
            className="w-16 px-2 py-1 rounded-lg border-2 border-black bg-white text-xs" />
          min
        </label>
      </div>

      <button type="submit" disabled={generating}
        className="neo-btn w-full py-3 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="pyssel-generate-btn">
        <Sparkles className="w-4 h-4" /> {generating ? 'AI:n virkar…' : 'Skapa mönster'}
      </button>
    </form>
  );
}

export default function Pyssel() {
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // {pattern, category, idea, level, isNew}
  const [openProject, setOpenProject] = useState(null); // {id, ...}
  const [filterCat, setFilterCat] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/pyssel/categories`, { withCredentials: true }),
        axios.get(`${API}/pyssel/projects`, { withCredentials: true }),
      ]);
      setCategories(c.data.categories || []);
      setLevels(c.data.levels || []);
      setProjects(p.data.projects || []);
    } catch (e) {
      toast.error('Kunde inte ladda pyssel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleGenerate = async (payload) => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/pyssel/generate`, payload, { withCredentials: true });
      setPreview({ ...data, isNew: true });
      toast.success('Mönstret är klart! 🌸');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI-fel');
    } finally {
      setGenerating(false);
    }
  };

  const savePreview = async () => {
    if (!preview) return;
    try {
      await axios.post(`${API}/pyssel/save`, {
        category: preview.category,
        title: preview.pattern.title || preview.idea,
        idea: preview.idea,
        level: preview.level,
        pattern: preview.pattern,
      }, { withCredentials: true });
      toast.success('Sparat i dina projekt 💗');
      setPreview(null);
      await loadAll();
    } catch (e) {
      toast.error('Kunde inte spara');
    }
  };

  const openProjectById = async (id) => {
    try {
      const { data } = await axios.get(`${API}/pyssel/project/${id}`, { withCredentials: true });
      setOpenProject(data.project);
    } catch (e) {
      toast.error('Kunde inte öppna');
    }
  };

  const toggleStep = async (i, done) => {
    if (!openProject) return;
    try {
      const { data } = await axios.post(`${API}/pyssel/project/${openProject.id}/progress`,
        { step_index: i, done }, { withCredentials: true });
      setOpenProject({ ...openProject, progress: data.progress });
    } catch (e) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Ta bort projektet?')) return;
    try {
      await axios.delete(`${API}/pyssel/project/${id}`, { withCredentials: true });
      toast.success('Borttaget');
      if (openProject?.id === id) setOpenProject(null);
      await loadAll();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar pyssel-verkstan…</div>;

  const filtered = filterCat ? projects.filter((p) => p.category === filterCat) : projects;
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));

  // Preview state (new pattern, not saved yet)
  if (preview && preview.isNew) {
    return (
      <div className="space-y-3" data-testid="pyssel-preview">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => setPreview(null)} className="neo-btn px-3 py-2 bg-white text-xs font-bold flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Tillbaka
          </button>
          <div className="flex gap-2">
            <button onClick={() => printPattern(preview.pattern, catMap[preview.category]?.label, catMap[preview.category]?.emoji, preview.level)}
              className="neo-btn px-3 py-2 bg-[#A2D2FF] text-xs font-bold flex items-center gap-1"
              data-testid="pyssel-print-preview">
              <Printer className="w-3.5 h-3.5" /> Skriv ut
            </button>
            <button onClick={() => { setPreview(null); }} className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold">
              Släng
            </button>
            <button onClick={savePreview}
              className="neo-btn px-4 py-2 bg-[#BBF7D0] text-xs font-bold flex items-center gap-1"
              data-testid="pyssel-save-preview">
              <Save className="w-3.5 h-3.5" /> Spara projekt
            </button>
          </div>
        </div>
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFC8DD] to-[#FDE8F5]">
          <p className="text-xs opacity-70">{catMap[preview.category]?.emoji} {catMap[preview.category]?.label} · {preview.level}</p>
          <h2 className="text-2xl font-bold font-['Fredoka']">{preview.pattern.title}</h2>
        </div>
        <PatternView pattern={preview.pattern} progress={null} category={preview.category} />
      </div>
    );
  }

  // Open saved project
  if (openProject) {
    return (
      <div className="space-y-3" data-testid="pyssel-project-view">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => setOpenProject(null)} className="neo-btn px-3 py-2 bg-white text-xs font-bold flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Tillbaka
          </button>
          <div className="flex gap-2">
            <button onClick={() => printPattern(openProject.pattern, catMap[openProject.category]?.label, catMap[openProject.category]?.emoji, openProject.level)}
              className="neo-btn px-3 py-2 bg-[#A2D2FF] text-xs font-bold flex items-center gap-1"
              data-testid="pyssel-print-project">
              <Printer className="w-3.5 h-3.5" /> Skriv ut
            </button>
            <button onClick={() => deleteProject(openProject.id)}
              className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Radera
            </button>
          </div>
        </div>
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFC8DD] to-[#FDE8F5]">
          <p className="text-xs opacity-70">{catMap[openProject.category]?.emoji} {catMap[openProject.category]?.label} · {openProject.level}</p>
          <h2 className="text-2xl font-bold font-['Fredoka']">{openProject.pattern?.title || openProject.title}</h2>
        </div>
        <PatternView pattern={openProject.pattern} progress={openProject.progress}
          onToggle={toggleStep} category={openProject.category} />
      </div>
    );
  }

  // Main hub view
  return (
    <div className="space-y-5" data-testid="pyssel-hub">
      <div className="neo-card p-5 bg-gradient-to-br from-[#FDE8F5] to-[#E8F4FF]">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-full border-4 border-black bg-white flex items-center justify-center text-3xl shrink-0">
            🧶
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold font-['Fredoka']">Pyssel-verkstan</h2>
            <p className="text-xs opacity-75 max-w-md">
              Skriv vad du och barnen vill skapa — AI:n gör ett steg-för-steg-mönster på svenska. Spara som projekt och bocka av allt eftersom. 🌸
            </p>
          </div>
        </div>
      </div>

      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <GenerateForm categories={categories} levels={levels}
          onGenerate={handleGenerate} generating={generating} />
      </div>

      {projects.length > 0 && (
        <div className="neo-card p-4 bg-[#FFFAF0] dark:bg-[#1f1f3a]">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-sm font-bold font-['Fredoka']">Dina projekt</p>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setFilterCat('')}
                className={`px-2.5 py-1 rounded-full border-2 border-black text-[10px] font-bold ${!filterCat ? 'bg-[#FFC8DD]' : 'bg-white'}`}>
                Alla
              </button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => setFilterCat(c.id)}
                  className={`px-2.5 py-1 rounded-full border-2 border-black text-[10px] font-bold ${filterCat === c.id ? 'bg-[#FFC8DD]' : 'bg-white'}`}>
                  {c.emoji}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => openProjectById(p.id)}
                className="neo-card p-3 bg-white dark:bg-[#252545] text-left hover:scale-[1.01] transition"
                data-testid={`pyssel-project-${p.id}`}>
                <div className="flex items-start gap-2">
                  <div className="text-2xl shrink-0">{catMap[p.category]?.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold font-['Fredoka'] truncate">{p.title}</p>
                    <p className="text-[10px] opacity-70">{catMap[p.category]?.label} · {p.level}</p>
                    <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-pink-400 to-emerald-400"
                        style={{ width: `${p.progress_percent}%` }} />
                    </div>
                    <p className="text-[10px] opacity-70 mt-0.5">{p.steps_done}/{p.steps_count} steg · {p.progress_percent}%</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
